/**
 * The staff-only grade badge: the letter in a coloured circle (A green,
 * B violet, C orange, D red, F solid red; never blue or yellow), with the
 * word ("Good", "At risk") in its label, so colour is never the only signal.
 * Shown on the patient header, the Family File, schedule cards, the hover
 * card, search results and patient lists.
 *
 * Hover shows the factors that cost points and how many ("No-shows −12
 * pts"), with the sub-scores; a click opens "Why this grade" (factors,
 * trend, what the grade does here), with "Details and actions" leading to
 * the grade window (override, excuse). Needs patient.view (the badge data
 * is never fetched without it). Privacy mode blurs the letter and shows no
 * explanation.
 */
import {
  GRADE_COMPONENT_KEYS,
  GRADE_FACTOR_LABEL,
  GRADE_WORD,
  type GradeAttendanceHistory,
  type GradeComponentKey,
  type PatientGrade,
} from '@velora/contracts/patient-grade';
import { Popover, Tooltip } from '@velora/ui';
import { Clock } from 'lucide-react';
import { QueryClientContext } from '@tanstack/react-query';
import { lazy, Suspense, useContext, useState } from 'react';
import { usePrivacy, useSkin } from '../../../app/shell.js';
import { usePatientGrade } from './api.js';
import './grade.css';

const GradeDialog = lazy(() => import('./GradeDialog.js'));
const GradeWhy = lazy(() => import('./GradeWhy.js'));

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export function gradeLabel(g: PatientGrade): string {
  return `Patient grade ${g.letter}${g.provisional ? ', provisional' : ''}${g.override ? ', staff override' : ''}`;
}

/** The label with the word: "Patient grade C (Fair)". */
export function gradeWordLabel(g: PatientGrade): string {
  return `${gradeLabel(g).replace(/^Patient grade ([A-F])/, `Patient grade $1 (${GRADE_WORD[g.letter]})`)}`;
}

type Reason = PatientGrade['reasons'][number];

const isFactor = (code: string): code is GradeComponentKey =>
  (GRADE_COMPONENT_KEYS as readonly string[]).includes(code);

/** A reason that cost points: its factor, as "No-shows −12 pts". Null for notes. */
export function reasonCost(r: Reason): string | null {
  if (r.points < 0.5 || !isFactor(r.code)) return null;
  return `${GRADE_FACTOR_LABEL[r.code]} −${Math.round(r.points)} pts`;
}

const n = (k: number, one: string, many = `${one}s`) => `${k} ${k === 1 ? one : many}`;

/**
 * The counts behind the attendance grade, last 24 months:
 * "Cancelled 3 (1 short notice) · 1 no-show · late 2 of 9 visits".
 */
export function historyText(h: GradeAttendanceHistory): string {
  const parts = [
    `Cancelled ${h.cancellations}${h.shortNotice > 0 ? ` (${h.shortNotice} short notice)` : ''}`,
    n(h.noShows, 'no-show'),
    h.timedVisits > 0
      ? `late ${h.lateArrivals} of ${n(h.timedVisits, 'visit')}`
      : 'no arrival times',
  ];
  return parts.join(' · ');
}

/** "Tends to arrive late (usually 15 min)"; null when the history does not support it. */
export function likelyLateText(h: GradeAttendanceHistory | null | undefined): string | null {
  if (!h?.likelyLate) return null;
  return `Tends to arrive late${h.typicalMinutesLate ? ` (usually ${h.typicalMinutesLate} min)` : ''}`;
}

/** The 24-month counts and the likely-late flag, for both hovers. */
function HistoryLines({ h, className }: { h: GradeAttendanceHistory; className: string }) {
  const late = likelyLateText(h);
  return (
    <>
      <span className={`${className}__hist`} data-testid="pg-history">
        Last 24 months: {historyText(h)}
      </span>
      {late ? (
        <span className={`${className}__late`} data-testid="pg-likely-late">
          <Clock size={12} aria-hidden /> {late}
        </span>
      ) : null}
    </>
  );
}

/** The hover: the letter, the two sub-scores, the factors that cost points and when it changed. */
export function GradeHover({ g }: { g: PatientGrade }) {
  const costs = g.reasons.filter((r) => reasonCost(r) !== null).slice(0, 5);
  const notes = g.reasons
    .filter((r) => r.code === 'provisional' || r.code === 'recovered' || r.code === 'override')
    .slice(0, 2);
  const shown: Reason[] = [...costs, ...notes];
  if (shown.length === 0) shown.push(...g.reasons.slice(0, 2));
  return (
    <span className="pg-hover">
      <b className="pg-hover__title">
        Grade {g.letter} · {GRADE_WORD[g.letter]}
        {g.provisional ? ' (provisional)' : ''}
      </b>
      <span className="pg-hover__subs">
        Attendance {g.attendance.letter}
        {g.attendance.score !== null ? ` ${Math.round(g.attendance.score)}` : ''} · Financial{' '}
        {g.financial.letter}
        {g.financial.score !== null ? ` ${Math.round(g.financial.score)}` : ''}
      </span>
      {g.history ? <HistoryLines h={g.history} className="pg-hover" /> : null}
      <span className="pg-hover__list">
        {shown.map((r) => {
          const cost = reasonCost(r);
          return (
            <span key={`${r.dimension}-${r.code}-${r.text}`} className="pg-hover__reason">
              {cost ? <b className="pg-hover__cost">{cost}</b> : null}
              <span>{r.text}</span>
            </span>
          );
        })}
      </span>
      {g.override ? (
        <span className="pg-hover__ov">
          Override by {g.override.byName}: {g.override.reason}
        </span>
      ) : null}
      <span className="pg-hover__when">
        {g.ungraded
          ? 'Not graded yet'
          : g.changedAt
            ? `Changed ${fmtDate(g.changedAt)}${g.previousLetter ? ` (was ${g.previousLetter})` : ''}`
            : `Graded ${fmtDate(g.computedAt)}`}
      </span>
      <span className="pg-hover__note">
        Staff only. Patients see office policies, never a grade. Click for why.
      </span>
    </span>
  );
}

export interface GradeBadgeProps {
  patientId: string | null | undefined;
  /**
   * Click opens "Why this grade" (default). False: hover only, for places
   * where a click must reach what is underneath.
   */
  interactive?: boolean;
  /** Where it sits, for sizing. */
  place?: 'card' | 'header' | 'list' | 'familyfile' | 'search';
  /** Show the word next to the letter ("C Fair"); on by default in the header. */
  withWord?: boolean;
}

/** Renders nothing outside a query client (isolated component tests, previews). */
export function GradeBadge(props: GradeBadgeProps) {
  return useContext(QueryClientContext) ? <GradeBadgeLive {...props} /> : null;
}

/** Clicks on a badge never select, drag or open what it sits on (cards, rows). */
const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

function GradeBadgeLive({
  patientId,
  interactive = true,
  place = 'list',
  withWord,
}: GradeBadgeProps) {
  const g = usePatientGrade(patientId);
  const { skin } = useSkin();
  const [why, setWhy] = useState(false);
  const [open, setOpen] = useState(false);
  const privacy = usePrivacy();
  if (!g || !patientId) return null;
  const word = withWord ?? place === 'header';
  const cls = `pg-badge pg-badge--${place}${skin === 'dentrix' ? ' pg-dtx pg-dtx--' + place : ''}${
    g.provisional ? ' is-provisional' : ''
  }${g.override ? ' is-override' : ''}`;
  const label = privacy.on ? 'Patient grade (hidden in privacy mode)' : gradeWordLabel(g);
  const letter = (
    <>
      {g.letter}
      {word ? <span className="pg-badge__word">{GRADE_WORD[g.letter]}</span> : null}
    </>
  );
  const tip = privacy.on ? label : <GradeHover g={g} />;
  if (!interactive) {
    return (
      <Tooltip content={tip} side="bottom" delayDuration={150}>
        <i
          className={cls}
          data-grade={g.letter}
          data-word={word ? '' : undefined}
          data-phi=""
          role="img"
          aria-label={label}
        >
          {letter}
        </i>
      </Tooltip>
    );
  }
  return (
    <>
      <Tooltip content={tip} side="bottom" delayDuration={150}>
        <span
          className="pg-badge-wrap"
          onPointerDown={stop}
          onMouseDown={stop}
          onClick={stop}
          onDoubleClick={stop}
          onContextMenu={stop}
        >
          <Popover
            open={why}
            onOpenChange={setWhy}
            side="bottom"
            align="start"
            label="Why this grade"
            className="pg-why-pop"
            trigger={
              <button
                type="button"
                className={cls}
                data-grade={g.letter}
                data-word={word ? '' : undefined}
                data-phi=""
                aria-label={`${label}. Why this grade`}
                aria-haspopup="dialog"
              >
                {letter}
              </button>
            }
          >
            {privacy.on ? (
              <p className="pg-why pg-why--loading">
                The grade explanation is hidden in privacy mode.
              </p>
            ) : why ? (
              <Suspense fallback={<p className="pg-why pg-why--loading">Loading the grade…</p>}>
                <GradeWhy
                  patientId={patientId}
                  onDetails={() => {
                    setWhy(false);
                    setOpen(true);
                  }}
                />
              </Suspense>
            ) : null}
          </Popover>
          {open ? (
            <Suspense fallback={null}>
              <GradeDialog patientId={patientId} open={open} onOpenChange={setOpen} />
            </Suspense>
          ) : null}
        </span>
      </Tooltip>
    </>
  );
}

/**
 * The schedule hover card's "Grade" row: the letter, the word, the
 * sub-scores, the factors that cost the most points, the 24-month
 * cancellation, no-show and late-arrival counts and "tends to arrive late". Plain marks (the hover card takes no pointer events). Privacy
 * mode shows that a grade exists, not what it is.
 */
export function GradeHoverRow({ patientId }: { patientId: string | null | undefined }) {
  return useContext(QueryClientContext) ? <GradeHoverRowLive patientId={patientId} /> : null;
}

function GradeHoverRowLive({ patientId }: { patientId: string | null | undefined }) {
  const g = usePatientGrade(patientId);
  const privacy = usePrivacy();
  if (!g) return null;
  const costs = g.reasons
    .map(reasonCost)
    .filter((c): c is string => c !== null)
    .slice(0, 3);
  const subs = `Attendance ${g.attendance.letter}${
    g.attendance.score !== null ? ` ${Math.round(g.attendance.score)}` : ''
  } · Financial ${g.financial.letter}${
    g.financial.score !== null ? ` ${Math.round(g.financial.score)}` : ''
  }`;
  return (
    <>
      <dt>Grade</dt>
      <dd className="pg-hcrow">
        {privacy.on ? (
          'Hidden in privacy mode'
        ) : (
          <>
            <i
              className={`pg-badge pg-badge--sm${g.provisional ? ' is-provisional' : ''}`}
              data-grade={g.letter}
              aria-hidden
            >
              {g.letter}
            </i>{' '}
            {GRADE_WORD[g.letter]}
            {g.provisional ? ' (provisional)' : ''}
            <small> · {subs}</small>
            {costs.length ? <small className="pg-hcrow__costs">{costs.join(' · ')}</small> : null}
            {g.history ? <HistoryLines h={g.history} className="pg-hcrow" /> : null}
          </>
        )}
      </dd>
    </>
  );
}
